/**
 * useChatService Hook
 * Manages WebSocket connection with HTTP fallback for chat communication
 * 
 * Features:
 * - WebSocket primary connection with auto-reconnection
 * - HTTP POST fallback when WebSocket fails
 * - Event emitter pattern for message handling
 * - Connection state management
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import {
    CONNECTION_STATES,
    EMITTED_EVENTS,
    EVENT_TYPES,
    STORAGE_KEYS,
    WEBSOCKET_CONFIG
} from '../constants';

/**
 * Hook for managing chat service connection (WebSocket + HTTP fallback)
 * @param {Object} options - Hook options
 * @param {boolean} options.autoConnect - Whether to auto-connect on mount
 * @returns {Object} Connection state and methods
 */
const useChatService = ({ autoConnect = false } = {}) => {
    // Connection state
    const [connectionState, setConnectionState] = useState(CONNECTION_STATES.DISCONNECTED);
    const [isUsingHTTP, setIsUsingHTTP] = useState(false);
    const [error, setError] = useState(null);

    // Refs for WebSocket and reconnection
    const wsRef = useRef(null);
    const reconnectAttemptsRef = useRef(0);
    const reconnectTimeoutRef = useRef(null);
    const pingIntervalRef = useRef(null);
    const eventHandlersRef = useRef(new Map());
    const connectWebSocketRef = useRef(null);

    // Message accumulation for streaming
    const currentMessageRef = useRef({ messageId: null, content: '' });

    // ========================================================================
    // Event Emitter
    // ========================================================================

    const emit = useCallback((event, data) => {
        const handlers = eventHandlersRef.current.get(event);
        if (handlers) {
            handlers.forEach(handler => {
                try {
                    handler(data);
                } catch (err) {
                    console.error('Chat service: Error in event handler:', err);
                }
            });
        }
    }, []);

    const on = useCallback((event, handler) => {
        if (!eventHandlersRef.current.has(event)) {
            eventHandlersRef.current.set(event, new Set());
        }
        eventHandlersRef.current.get(event).add(handler);
        return () => {
            eventHandlersRef.current.get(event)?.delete(handler);
        };
    }, []);

    const off = useCallback((event, handler) => {
        eventHandlersRef.current.get(event)?.delete(handler);
    }, []);

    // ========================================================================
    // WebSocket URL
    // ========================================================================

    const getWebSocketURL = useCallback(() => {
        const devUrl = WEBSOCKET_CONFIG.DEV_BASE_URL;
        const protocol = devUrl ? 'ws:' : (window.location.protocol === 'https:' ? 'wss:' : 'ws:');
        const host = devUrl || window.location.host;
        let url = `${protocol}//${host}${WEBSOCKET_CONFIG.ENDPOINT_PATH}`;

        const token = localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN);
        if (token) {
            url += `?token=${encodeURIComponent(token)}`;
        }

        return url;
    }, []);

    const getHTTPURL = useCallback(() => {
        const devUrl = WEBSOCKET_CONFIG.DEV_BASE_URL;
        const origin = devUrl ? `http://${devUrl}` : window.location.origin;
        return `${origin}${WEBSOCKET_CONFIG.HTTP_ENDPOINT_PATH}`;
    }, []);

    // ========================================================================
    // Message Handling
    // ========================================================================

    const handleMessage = useCallback((data) => {
        try {
            const event = typeof data === 'string' ? JSON.parse(data) : data;

            // Skip pong responses
            if (event.type === 'pong') return;

            console.log('Chat service: Received event:', event.type, event);

            // Route to specific handlers based on AG-UI spec
            switch (event.type) {
                case EVENT_TYPES.RUN_STARTED: {
                    // Props: threadId, runId, parentRunId (optional), input (optional)
                    const { threadId, runId, parentRunId, input } = event.data || {};
                    emit(EMITTED_EVENTS.RUN_STARTED, { threadId, runId, parentRunId, input });
                    break;
                }
                case EVENT_TYPES.RUN_FINISHED: {
                    // Props: threadId, runId, result (optional)
                    const { threadId, runId, result } = event.data || {};
                    emit(EMITTED_EVENTS.RUN_FINISHED, { threadId, runId, result });
                    break;
                }
                case EVENT_TYPES.RUN_ERROR: {
                    // Props: message, code (optional)
                    const { message, code } = event.data || {};
                    emit(EMITTED_EVENTS.RUN_ERROR, { message, code });
                    break;
                }
                case EVENT_TYPES.STEP_STARTED: {
                    // Props: stepName
                    const { stepName } = event.data || {};
                    emit(EMITTED_EVENTS.STEP_STARTED, { stepName });
                    break;
                }
                case EVENT_TYPES.STEP_FINISHED: {
                    // Props: stepName
                    const { stepName } = event.data || {};
                    emit(EMITTED_EVENTS.STEP_FINISHED, { stepName });
                    break;
                }
                case EVENT_TYPES.TEXT_MESSAGE_START: {
                    // Props: messageId, role ("developer", "system", "assistant", "user", "tool")
                    const { messageId, role } = event.data || {};
                    currentMessageRef.current = { messageId, content: '', role };
                    emit(EMITTED_EVENTS.TEXT_MESSAGE_START, { messageId, role });
                    break;
                }
                case EVENT_TYPES.TEXT_MESSAGE_CONTENT: {
                    // Props: messageId, delta (non-empty text chunk)
                    const { messageId, delta } = event.data || {};
                    if (currentMessageRef.current.messageId === messageId) {
                        currentMessageRef.current.content += delta || '';
                    }
                    emit(EMITTED_EVENTS.TEXT_MESSAGE_CONTENT, {
                        messageId,
                        delta,
                        fullContent: currentMessageRef.current.content
                    });
                    break;
                }
                case EVENT_TYPES.TEXT_MESSAGE_END: {
                    const { messageId } = event.data || {};
                    emit(EMITTED_EVENTS.TEXT_MESSAGE_END, {
                        messageId,
                        content: currentMessageRef.current.content,
                        role: currentMessageRef.current.role
                    });
                    currentMessageRef.current = { messageId: null, content: '', role: null };
                    break;
                }
                case EVENT_TYPES.TEXT_MESSAGE_CHUNK: {
                    // Convenience event - auto-expands to Start → Content → End
                    // First chunk must include messageId and role
                    const { messageId, role, delta } = event.data || {};

                    // If this is first chunk (has role), treat as start
                    if (role && messageId) {
                        currentMessageRef.current = { messageId, content: '', role };
                        emit(EMITTED_EVENTS.TEXT_MESSAGE_START, { messageId, role });
                    }

                    // Add content if delta exists
                    if (delta) {
                        const msgId = messageId || currentMessageRef.current.messageId;
                        if (currentMessageRef.current.messageId === msgId) {
                            currentMessageRef.current.content += delta;
                        }
                        emit(EMITTED_EVENTS.TEXT_MESSAGE_CONTENT, {
                            messageId: msgId,
                            delta,
                            fullContent: currentMessageRef.current.content
                        });
                    }
                    break;
                }
                default:
                    console.log('Chat service: Unhandled event type:', event.type);
            }

            // Emit raw event for custom handling
            emit(EMITTED_EVENTS.EVENT, event);
        } catch (err) {
            console.error('Chat service: Error parsing message:', err);
            emit(EMITTED_EVENTS.PARSE_ERROR, { error: err, data });
        }
    }, [emit]);

    // ========================================================================
    // WebSocket Management
    // ========================================================================

    const startPingInterval = useCallback(() => {
        pingIntervalRef.current = setInterval(() => {
            if (wsRef.current?.readyState === WebSocket.OPEN) {
                wsRef.current.send(JSON.stringify({ type: 'ping' }));
            }
        }, WEBSOCKET_CONFIG.PING_INTERVAL);
    }, []);

    const stopPingInterval = useCallback(() => {
        if (pingIntervalRef.current) {
            clearInterval(pingIntervalRef.current);
            pingIntervalRef.current = null;
        }
    }, []);

    const cleanup = useCallback(() => {
        if (reconnectTimeoutRef.current) {
            clearTimeout(reconnectTimeoutRef.current);
            reconnectTimeoutRef.current = null;
        }
        stopPingInterval();
        if (wsRef.current) {
            wsRef.current.onclose = null;
            wsRef.current.close();
            wsRef.current = null;
        }
        currentMessageRef.current = { messageId: null, content: '', role: null };
    }, [stopPingInterval]);

    const handleReconnect = useCallback(() => {
        if (reconnectAttemptsRef.current >= WEBSOCKET_CONFIG.MAX_RECONNECT_ATTEMPTS) {
            console.log('Chat service: Max reconnect attempts reached, falling back to HTTP');
            setConnectionState(CONNECTION_STATES.CONNECTED);
            setIsUsingHTTP(true);
            emit(EMITTED_EVENTS.FALLBACK_TO_HTTP);
            return;
        }

        reconnectAttemptsRef.current++;
        const delay = Math.min(
            WEBSOCKET_CONFIG.RECONNECT_BASE_DELAY * Math.pow(2, reconnectAttemptsRef.current - 1),
            WEBSOCKET_CONFIG.MAX_RECONNECT_DELAY
        );

        console.log(`Chat service: Reconnecting in ${delay}ms (attempt ${reconnectAttemptsRef.current})`);
        setConnectionState(CONNECTION_STATES.RECONNECTING);

        reconnectTimeoutRef.current = setTimeout(() => {
            connectWebSocketRef.current?.();
        }, delay);
    }, [emit]);

    const connectWebSocket = useCallback(() => {
        return new Promise((resolve, reject) => {
            if (wsRef.current?.readyState === WebSocket.OPEN) {
                resolve();
                return;
            }

            cleanup();
            setConnectionState(CONNECTION_STATES.CONNECTING);
            setError(null);

            const wsURL = getWebSocketURL();
            console.log('Chat service: Connecting to WebSocket:', wsURL);

            try {
                wsRef.current = new WebSocket(wsURL);

                wsRef.current.onopen = () => {
                    console.log('Chat service: WebSocket connected');
                    reconnectAttemptsRef.current = 0;
                    setConnectionState(CONNECTION_STATES.CONNECTED);
                    setIsUsingHTTP(false);
                    startPingInterval();
                    emit(EMITTED_EVENTS.CONNECTED);
                    resolve();
                };

                wsRef.current.onclose = (event) => {
                    console.log('Chat service: WebSocket closed', event.code, event.reason);
                    stopPingInterval();
                    setConnectionState(CONNECTION_STATES.DISCONNECTED);
                    emit(EMITTED_EVENTS.DISCONNECTED, { code: event.code, reason: event.reason });
                    handleReconnect();
                };

                wsRef.current.onerror = (err) => {
                    console.error('Chat service: WebSocket error', err);
                    setConnectionState(CONNECTION_STATES.ERROR);
                    setError(new Error('WebSocket connection error'));
                    reject(err);
                };

                wsRef.current.onmessage = (event) => {
                    handleMessage(event.data);
                };
            } catch (err) {
                console.error('Chat service: Failed to create WebSocket', err);
                setConnectionState(CONNECTION_STATES.ERROR);
                setError(err);
                reject(err);
            }
        });
    }, [cleanup, getWebSocketURL, startPingInterval, stopPingInterval, handleMessage, handleReconnect, emit]);

    // Store ref to connectWebSocket for reconnection
    connectWebSocketRef.current = connectWebSocket;

    // ========================================================================
    // HTTP Fallback
    // ========================================================================

    const sendMessageHTTP = useCallback(async (messages, conversationId = null, mode = 'discuss') => {
        const httpURL = getHTTPURL();

        // Format payload per spec
        const request = {
            messages: messages.map(m => ({
                role: m.role,
                content: m.content,
                timestamp: m.timestamp || Date.now()
            })),
            stream: false,
            mode,
            enable_llm_detection: false,
            conversation_id: conversationId || ''
        };

        console.log('Chat service: Sending message via HTTP', request);

        // Emit run started for UI feedback
        emit(EMITTED_EVENTS.RUN_STARTED, { threadId: conversationId });
        emit(EMITTED_EVENTS.STEP_STARTED, { stepName: 'Thinking' });

        try {
            const response = await fetch(httpURL, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN) || ''}`
                },
                body: JSON.stringify(request)
            });

            if (!response.ok) {
                throw new Error(`HTTP error: ${response.status}`);
            }

            const data = await response.json();

            // Handle response per spec: { conversation_id, message: { role, content, agent, intent }, meta_data }
            if (data.message && data.message.content) {
                const messageId = `msg-${Date.now()}`;
                const content = data.message.content;
                const role = data.message.role || 'assistant';

                emit(EMITTED_EVENTS.TEXT_MESSAGE_START, { messageId, role });
                emit(EMITTED_EVENTS.TEXT_MESSAGE_CONTENT, { messageId, delta: content, fullContent: content });
                emit(EMITTED_EVENTS.TEXT_MESSAGE_END, { messageId, content, role });
            } else if (data.events && Array.isArray(data.events)) {
                // Handle array of events if returned
                data.events.forEach(event => handleMessage(event));
            }

            emit(EMITTED_EVENTS.STEP_FINISHED, { stepName: 'Thinking' });
            emit(EMITTED_EVENTS.RUN_FINISHED, {
                threadId: data.conversation_id || conversationId,
                result: data
            });

            return data;
        } catch (err) {
            console.error('Chat service: HTTP request failed', err);
            // Don't emit runError for network failures - keep step indicator showing for demo
            // In production, you may want to emit runError here
            // emit('runError', { message: err.message });
            throw err;
        }
    }, [getHTTPURL, emit, handleMessage]);

    // ========================================================================
    // Public Methods
    // ========================================================================

    const connect = useCallback(async () => {
        try {
            setError(null);
            await connectWebSocket();
        } catch (err) {
            setError(err);
            // If WebSocket fails immediately, switch to HTTP
            setIsUsingHTTP(true);
            setConnectionState(CONNECTION_STATES.CONNECTED);
        }
    }, [connectWebSocket]);

    const disconnect = useCallback(() => {
        cleanup();
        reconnectAttemptsRef.current = 0;
        setConnectionState(CONNECTION_STATES.DISCONNECTED);
        setIsUsingHTTP(false);
        setError(null);
        emit(EMITTED_EVENTS.DISCONNECTED, { manual: true });
    }, [cleanup, emit]);

    const sendMessage = useCallback(async (messages, conversationId = null, mode = 'discuss') => {
        // Use HTTP if WebSocket is not available
        if (isUsingHTTP || !wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
            return sendMessageHTTP(messages, conversationId, mode);
        }

        return new Promise((resolve, reject) => {
            // WebSocket payload per spec
            const request = {
                thread_id: conversationId || '',
                run_id: '',
                messages: messages.map(m => ({
                    role: m.role,
                    content: m.content
                })),
                conversation_id: conversationId || '',
                mode,
                auto_approval: true
            };

            console.log('Chat service: Sending message via WebSocket', request);

            // Set up completion listeners
            const runFinishedHandler = (data) => {
                off(EMITTED_EVENTS.RUN_FINISHED, runFinishedHandler);
                off(EMITTED_EVENTS.RUN_ERROR, runErrorHandler);
                resolve(data);
            };

            const runErrorHandler = (data) => {
                off(EMITTED_EVENTS.RUN_FINISHED, runFinishedHandler);
                off(EMITTED_EVENTS.RUN_ERROR, runErrorHandler);
                reject(new Error(data.message || 'Run error'));
            };

            on(EMITTED_EVENTS.RUN_FINISHED, runFinishedHandler);
            on(EMITTED_EVENTS.RUN_ERROR, runErrorHandler);

            wsRef.current.send(JSON.stringify(request));
        });
    }, [isUsingHTTP, sendMessageHTTP, on, off]);

    // ========================================================================
    // Auto-connect on mount
    // ========================================================================

    useEffect(() => {
        if (autoConnect) {
            connect();
        }

        return () => {
            cleanup();
        };
    }, [autoConnect]); // eslint-disable-line react-hooks/exhaustive-deps

    // ========================================================================
    // Return
    // ========================================================================

    return {
        // State
        connectionState,
        isConnected: connectionState === CONNECTION_STATES.CONNECTED,
        isConnecting: connectionState === CONNECTION_STATES.CONNECTING ||
            connectionState === CONNECTION_STATES.RECONNECTING,
        isDisconnected: connectionState === CONNECTION_STATES.DISCONNECTED,
        hasError: connectionState === CONNECTION_STATES.ERROR,
        isUsingHTTP,
        error,

        // Actions
        connect,
        disconnect,
        sendMessage,

        // Event subscription
        on,
        off
    };
};

export default useChatService;
